import Repository from "@/repository";
import { getCarNumberSaveValue } from "@/utils/carNumber";
import { clearEmpty } from "@/utils/removeEmpty";
import { normalizeErrors } from "@/utils/validation";

const preserveNullPaths = [
  "client",
  "client_data.name",
  "client_data.phone_number",
  "car.tires.second_tires",
  "car.tires.summer_tires.second_tires",
  "car.tires.winter_tires.second_tires",
];

const defaultOrder = {
  source: "site",
  payment_type: "cash",
  fast_source: "online",
  client_data: {},
  client: null,
  car: {
    tires: {
      use_same_tires: true,
      summer_tires: {
        first_tires: null,
        is_alloy_wheel: false,
        is_assembled_tire: false,
        is_active: true,
      },
      winter_tires: {
        first_tires: null,
        is_alloy_wheel: false,
        is_assembled_tire: false,
        is_active: false,
      },
    },
  },
};

const defaultCar = {
  make: "",
  model: "",
  generation: "",
  first_tires: "",
  second_tires: "",
};

const defaultOfferSettings = {
  include_runflat: false,
  include_pressure_sensor: false,
  include_alloy_wheel: false,
  touched: false,
};

const setNestedField = (target, field, value) => {
  const keys = field.split(".");
  let current = target;

  keys.slice(0, -1).forEach((key) => {
    if (!(key in current) || current[key] == null) {
      current[key] = {};
    }
    current = current[key];
  });

  current[keys[keys.length - 1]] = value;
};

const clearNestedField = (target, field) => {
  const keys = field.split(".");
  const stack = [];
  let current = target;

  for (const key of keys.slice(0, -1)) {
    if (!current?.[key] || typeof current[key] !== "object") {
      return;
    }
    stack.push([current, key]);
    current = current[key];
  }

  delete current?.[keys[keys.length - 1]];

  for (let index = stack.length - 1; index >= 0; index -= 1) {
    const [parent, key] = stack[index];
    if (parent[key] && !Object.keys(parent[key]).length) {
      delete parent[key];
    }
  }
};

const normalizeId = (value) => {
  if (!value) return value;
  if (typeof value === "object") return value.id ?? null;
  return value;
};

const resolveSettingFastId = (order) =>
  normalizeId(order?.setting_fast_id ?? order?.setting_fast);

const QUEUE_STATUSES = ["complete", "in progress", "request"];
const ACTIVE_QUEUE_STATUSES = ["in progress", "request"];

const normalizeQueueOrderStatus = (status) =>
  String(status || "")
    .trim()
    .toLowerCase()
    .replace(/_/g, " ");

const isActiveQueueStatus = (status) => {
  const normalizedStatus = normalizeQueueOrderStatus(status);
  return ACTIVE_QUEUE_STATUSES.includes(normalizedStatus);
};

const isDoneQueueStatus = (status) =>
  normalizeQueueOrderStatus(status) === "complete";

const getPlannedAtTime = (order) => {
  if (!order?.planned_at) return Number.POSITIVE_INFINITY;
  const time = new Date(order.planned_at).getTime();
  return Number.isFinite(time) ? time : Number.POSITIVE_INFINITY;
};

const compareOrdersByPlannedAt = (first, second) =>
  getPlannedAtTime(first) - getPlannedAtTime(second);

const sortOrdersByPlannedAt = (orders) => {
  if (!Array.isArray(orders)) return [];
  return [...orders].sort(compareOrdersByPlannedAt);
};

const sanitizePhoneNumber = (value) => {
  const raw = String(value || "");
  if (!raw.trim()) return "";
  if (raw.trim().startsWith("+")) {
    const digits = raw.replace(/[^\d]/g, "");
    return digits ? `+${digits}` : "+";
  }
  return raw.replace(/[^\d]/g, "");
};

const sanitizeOrderPayload = (order, { includeComplex = false } = {}) => {
  const nextOrder = JSON.parse(JSON.stringify(order || {}));
  delete nextOrder.include_runflat;
  delete nextOrder.include_pressure_sensor;
  delete nextOrder.include_alloy_wheel;
  delete nextOrder.offers;
  if (!nextOrder.planned_at) {
    delete nextOrder.planned_at;
  }
  if (!includeComplex) {
    delete nextOrder.complex;
  }
  if (nextOrder.client_data?.phone_number) {
    nextOrder.client_data.phone_number = sanitizePhoneNumber(
      nextOrder.client_data.phone_number,
    );
  }
  if (nextOrder.car) {
    nextOrder.car.number = getCarNumberSaveValue(nextOrder.car.number);
  }
  return nextOrder;
};

export default {
  namespaced: true,

  state: {
    order: JSON.parse(JSON.stringify(defaultOrder)),
    car: { ...defaultCar },
    offerSettings: { ...defaultOfferSettings },
    errors: {},
    workTimes: [],
    carMakes: [],
    queue: [],
    doneOrders: [],
  },

  getters: {
    queue(state) {
      return state.queue.map((item) => ({
        ...item,
        orders: sortOrdersByPlannedAt(
          item.orders?.filter((order) => isActiveQueueStatus(order.status)),
        ),
      }));
    },
    doneOrders(state) {
      return state.queue
        .map((item) => ({
          ...item,
          orders: sortOrdersByPlannedAt(
            item.orders?.filter((order) => isDoneQueueStatus(order.status)),
          ),
        }))
        .filter((item) => item.orders?.length);
    },
  },

  actions: {
    async saveOrder({ state, dispatch }, options = {}) {
      if (!state.order.id) {
        return dispatch("createOrder", options);
      }
      return dispatch("updateOrder", options);
    },

    async createOrder({ state, commit }, options = {}) {
      const order = clearEmpty(sanitizeOrderPayload(state.order, options), {
        preserveNullPaths,
      });
      const { value, error } = await Repository.Orders.createOrder(order);
      if (!error && value) {
        commit("setOrder", value);
        commit("setErrors", {});
      }
      if (error) {
        commit("setErrors", error);
      }
      return { value, error };
    },

    async confirmOrder({ state, commit }, { id, includeComplex = false }) {
      if (!id) {
        return;
      }
      const order = clearEmpty(
        sanitizeOrderPayload(state.order, { includeComplex }),
        {
        preserveNullPaths,
        },
      );
      const { value, error } = await Repository.Orders.confirmOrder(id, order);
      if (!error && value) {
        commit("setOrder", value);
        commit("setErrors", {});
      }
      if (error) {
        commit("setErrors", error);
      }
      return { value, error };
    },

    async addOffers({ state, commit }, { ids }) {
      const { value, error } = await Repository.Orders.addOffers({
        id: state.order.id,
        ids,
      });
      return { value, error };
    },

    async clearOrderOffers({ state, commit }) {
      const ids = (state.order?.offers || [])
        .map((item) => item?.id)
        .filter(Boolean);

      if (!state.order?.id || !ids.length) {
        commit("setOrderOffers", []);
        return { value: [], error: null };
      }

      const { value, error } = await Repository.Orders.removeOffers({
        id: state.order.id,
        ids,
      });

      if (!error) {
        commit("setOrderOffers", []);
        commit("setErrors", {});
      }

      if (error) {
        commit("setErrors", error);
      }

      return { value, error };
    },

    async patchOffers({ state, commit }, { payload }) {
      if (!state.order.id || !Array.isArray(payload) || !payload.length) {
        return { value: [], error: null };
      }

      const { value, error } = await Repository.Orders.patchOffers({
        id: state.order.id,
        payload,
      });

      if (!error && Array.isArray(value)) {
        commit("setOrderOffers", value);
        commit("setErrors", {});
      }

      if (error) {
        commit("setErrors", error);
      }

      return { value, error };
    },

    async updateOrder({ state, commit }, options = {}) {
      const order = clearEmpty(sanitizeOrderPayload(state.order, options), {
        preserveNullPaths,
      });
      const { value, error } = await Repository.Orders.updateOrder(order);
      if (!error && value) {
        commit("setOrder", value);
        commit("setErrors", {});
      }
      if (error) {
        commit("setErrors", error);
      }
      return { value, error };
    },

    async fetchWorkTimes({ commit }, { date, id }) {
      if (!id) {
        return;
      }
      const { value, error } = await Repository.Orders.getWorkTimes(date, id);
      if (!error && value) {
        commit("setWorkTimes", value);
      }
      return { value, error };
    },

    async fetchCarMakes({ commit }, { title }) {
      const { value, error } = await Repository.Orders.getCarMakes(title);
      if (!error && value) {
        commit("setCarMakes", value);
      }
      return { value, error };
    },

    async fetchQueue({ commit }, params = {}) {
      const { value, error } = await Repository.Orders.get({
        ...params,
        status: QUEUE_STATUSES,
      });
      if (!error && value) {
        commit("setQueue", value);
      }
      return { value, error };
    },
  },

  mutations: {
    setOrderField(state, { field, value }) {
      setNestedField(state.order, field, value);
    },
    setCarField(state, { field, value }) {
      state.car[field] = value;
    },
    setOfferSettings(state, value) {
      state.offerSettings = {
        ...state.offerSettings,
        ...value,
      };
    },
    setOrder(state, value) {
      state.order = value;

      if (!value?.car) {
        return;
      }

      if (value.car.car_type && typeof value.car.car_type === "object") {
        state.order.car.car_type = value.car.car_type.slug;
      }
      if (
        value.car.tires?.tire_type &&
        typeof value.car.tires.tire_type === "object"
      ) {
        state.order.car.tires.tire_type = value.car.tires.tire_type.slug;
      }

      state.order.car.make = normalizeId(value.car.make);
      state.order.car.model = normalizeId(value.car.model);
      state.order.car.generation = normalizeId(value.car.generation);

      state.car.make = value.car.make?.title || state.car.make || "";
      state.car.model = value.car.model?.title || state.car.model || "";
      state.car.generation =
        value.car.generation?.title || state.car.generation || "";

      const tires = value.car.tires || {};
      const summerTire = tires.summer_tires?.first_tires;
      const rootTire = tires.first_tires;
      const tireObj = summerTire || rootTire;
      if (tireObj) {
        if (!state.order.car.tires) {
          state.order.car.tires = {};
        }
        if (!state.order.car.tires.summer_tires) {
          state.order.car.tires.summer_tires = { is_active: true };
        }
        const tireId = normalizeId(tireObj);
        state.order.car.tires.summer_tires.first_tires = tireId;
        state.order.car.tires.first_tires = tireId;
        state.car.first_tires = tireObj?.title || state.car.first_tires || "";
      }

      const summerSecondTire = tires.summer_tires?.second_tires;
      const rootSecondTire = tires.second_tires;
      const secondTireObj = summerSecondTire || rootSecondTire;
      if (secondTireObj) {
        if (!state.order.car.tires) {
          state.order.car.tires = {};
        }
        if (!state.order.car.tires.summer_tires) {
          state.order.car.tires.summer_tires = { is_active: true };
        }
        const secondTireId = normalizeId(secondTireObj);
        state.order.car.tires.summer_tires.second_tires = secondTireId;
        state.order.car.tires.second_tires = secondTireId;
        state.car.second_tires =
          secondTireObj?.title || state.car.second_tires || "";
      } else if (state.order.car.tires?.summer_tires) {
        state.order.car.tires.summer_tires.second_tires = null;
        state.order.car.tires.second_tires = null;
        state.car.second_tires = "";
      }
    },
    updateOrders(state, order) {
      const orderId = String(order.id);
      const isQueueVisible =
        isActiveQueueStatus(order.status) || isDoneQueueStatus(order.status);
      const settingFastId = resolveSettingFastId(order);

      let currentColumn = null;
      let currentIndex = -1;

      for (const column of state.queue) {
        if (!column.orders) continue;
        const orderIndex = column.orders.findIndex(
          (o) => String(o.id) === orderId,
        );
        if (orderIndex > -1) {
          currentColumn = column;
          currentIndex = orderIndex;
          break;
        }
      }

      if (currentColumn && currentIndex > -1) {
        if (!isQueueVisible) {
          currentColumn.orders.splice(currentIndex, 1);
          return;
        }

        if (
          String(currentColumn.setting_fast?.id) === String(settingFastId)
        ) {
          currentColumn.orders.splice(currentIndex, 1, order);
          currentColumn.orders.sort(compareOrdersByPlannedAt);
          return;
        }

        currentColumn.orders.splice(currentIndex, 1);
      } else if (!isQueueVisible) {
        return;
      }

      if (!settingFastId) return;

      const column = state.queue.find(
        (item) => String(item.setting_fast?.id) === String(settingFastId),
      );
      if (!column) return;

      if (!column.orders) {
        column.orders = [];
      }
      column.orders.push(order);
      column.orders.sort(compareOrdersByPlannedAt);
    },
    setWorkTimes(state, value) {
      state.workTimes = value;
    },
    setOrderOffers(state, value) {
      if (!state.order) {
        state.order = JSON.parse(JSON.stringify(defaultOrder));
      }

      state.order.offers = Array.isArray(value) ? value : [];
    },
    resetOrder(state) {
      state.order = JSON.parse(JSON.stringify(defaultOrder));
      state.car = { ...defaultCar };
      state.offerSettings = { ...defaultOfferSettings };
      state.errors = {};
      state.workTimes = [];
    },
    setErrors(state, value) {
      state.errors = normalizeErrors(value);
    },
    setFieldError(state, { field, value }) {
      setNestedField(state.errors, field, value);
    },
    clearFieldError(state, field) {
      clearNestedField(state.errors, field);
    },
    setCarMakes(state, value) {
      state.carMakes = value;
    },
    setQueue(state, value) {
      state.queue = (Array.isArray(value) ? value : []).map((item) => ({
        ...item,
        orders: sortOrdersByPlannedAt(item?.orders),
      }));
    },
  },
};
